const clamp = (value, minimum, maximum) => Math.min(Math.max(value, minimum), maximum);

export const getDocumentSelectionPopoverLayout = ({
    triggerRect,
    popoverWidth = 440,
    popoverHeight = 390,
    viewportWidth,
    viewportHeight,
    margin = 12,
    gap = 8,
}) => {
    const width = Math.max(0, Math.min(
        Number(popoverWidth) || 0,
        Math.max(0, (Number(viewportWidth) || 0) - (margin * 2))
    ));
    const left = clamp(
        Number(triggerRect?.left) || 0,
        margin,
        Math.max(margin, (Number(viewportWidth) || 0) - width - margin)
    );
    const spaceBelow = Math.max(
        0,
        (Number(viewportHeight) || 0) - (Number(triggerRect?.bottom) || 0) - gap - margin
    );
    const spaceAbove = Math.max(0, (Number(triggerRect?.top) || 0) - gap - margin);
    const showAbove = spaceBelow < Math.min(Number(popoverHeight) || 0, 240)
        && spaceAbove > spaceBelow;
    const availableHeight = showAbove ? spaceAbove : spaceBelow;

    return {
        left,
        width,
        maxHeight: Math.min(Number(popoverHeight) || 0, availableHeight),
        placement: showAbove ? 'above' : 'below',
        ...(showAbove
            ? {
                top: 'auto',
                bottom: Math.max(margin, (Number(viewportHeight) || 0) - (Number(triggerRect?.top) || 0) + gap),
            }
            : {
                top: Math.max(margin, (Number(triggerRect?.bottom) || 0) + gap),
                bottom: 'auto',
            }),
    };
};

// Adapted from the preserved multi-folder selection work. Selection keys are
// authoritative storage paths, never paths relative to the currently open folder.
export const uniqueDocumentSelectionKeys = (keys) => Array.from(new Set(
    (Array.isArray(keys) ? keys : []).filter(key => typeof key === 'string' && key.length > 0)
));

export const toggleVisibleDocumentSelection = ({ selectedKeys, visibleKeys }) => {
    const current = uniqueDocumentSelectionKeys(selectedKeys);
    const visible = uniqueDocumentSelectionKeys(visibleKeys);
    if (visible.length > 0 && visible.every(key => current.includes(key))) {
        const visibleSet = new Set(visible);
        return current.filter(key => !visibleSet.has(key));
    }
    return uniqueDocumentSelectionKeys([...current, ...visible]);
};

export const buildSelectedDocumentItems = (selectedKeys, knownItems = []) => {
    const byKey = new Map(knownItems.map(item => [item?.key || item?.file?.key, item]));
    return uniqueDocumentSelectionKeys(selectedKeys).map(key => {
        const item = byKey.get(key);
        const isFolder = item?.type === 'folder' || key.endsWith('/');
        return {
            ...item,
            key,
            type: isFolder ? 'folder' : 'file',
            name: item?.name || item?.file?.name || key.replace(/\/+$/, '').split('/').pop(),
            ...(isFolder ? {
                folderPath: key.replace(/^(?:cases|leads)\/[^/]+\//, '').replace(/\/+$/, ''),
            } : {}),
        };
    });
};

export const buildDocumentSelectionDisplayRows = (selectedKeys, folderName) => {
    const currentFolder = String(folderName || '').replace(/^\/+|\/+$/g, '')
        .replace(/^\{\{Name\}\}(?:\/|$)/, '').replace(/^root$/i, '');
    return uniqueDocumentSelectionKeys(selectedKeys).map(key => {
        const relative = key.replace(/^(?:cases|leads)\/[^/]+\//, '')
            .replace(/^\{\{Name\}\}(?:\/|$)/, '').replace(/\/+$/, '');
        const parts = relative.split('/');
        const name = parts.pop() || key;
        const parentPath = parts.join('/');
        return { key, name, parentPath, isCurrentFolder: parentPath === currentFolder };
    });
};
